import type { Theme } from '../stores/themeStore';
import type { ThemeSettings } from '../stores/themeSettingsStore';

/**
 * Update the manifest theme colors based on current theme and settings
 * Updates the theme-color meta tag dynamically based on current theme
 * Note: The manifest.json file itself is static (generated by vite-plugin-pwa),
 * but the theme-color meta tag is updated dynamically for browser UI theming
 * @param theme Current theme (light or dark)
 * @param settings Theme settings with color values
 */
export function updateManifestColors(theme: Theme, settings: ThemeSettings): void {
  if (typeof document === 'undefined') return;

  // Determine theme color based on current theme
  // This is used for browser UI theming (address bar, etc.)
  const themeColor = theme === 'dark'
    ? (settings.accentDark || settings.accent)
    : settings.accent;

  // Update theme-color meta tag
  // This is the most important for dynamic theming as it affects browser UI
  let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;
  if (!themeColorMeta) {
    themeColorMeta = document.createElement('meta');
    themeColorMeta.name = 'theme-color';
    document.head.appendChild(themeColorMeta);
  }
  themeColorMeta.content = themeColor;
}

/**
 * Initialize the manifest manager
 * Sets up subscriptions to theme and theme settings stores
 */
export function initializeManifestManager(
  themeStore: { subscribe: (callback: (value: Theme) => void) => () => void },
  themeSettingsStore: { subscribe: (callback: (value: ThemeSettings) => void) => () => void }
): () => void {
  if (typeof document === 'undefined') {
    return () => {}; // Return no-op unsubscribe function
  }

  let currentTheme: Theme = 'dark';
  let currentSettings: ThemeSettings;

  // Subscribe to theme changes
  const unsubscribeTheme = themeStore.subscribe((theme) => {
    currentTheme = theme;
    if (currentSettings) {
      updateManifestColors(currentTheme, currentSettings);
    }
  });

  // Subscribe to theme settings changes
  const unsubscribeSettings = themeSettingsStore.subscribe((settings) => {
    currentSettings = settings;
    updateManifestColors(currentTheme, settings);
  });

  // Return cleanup function
  return () => {
    unsubscribeTheme();
    unsubscribeSettings();
  };
}
